import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NovedadesService } from 'src/app/service/catalogo/novedades.service';

interface SearchFilters {
  query: string;
  includePhotos: boolean;
  includeCovers: boolean;
  categoryName: string;
  subCategoryName: string;
  yearFrom: number;
  yearTo: number;
  dateFrom: string;
  dateTo: string;
  sortBy: string;
}

interface SubCategory {
  id: string;
  name: string;
  descript: string;
}

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss']
})
export class AdvancedSearchComponent implements OnInit {
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() searchPerformed = new EventEmitter<any>();

  searchFilters: SearchFilters = {
    query: '',
    includePhotos: true,
    includeCovers: true,
    categoryName: '',
    subCategoryName: '',
    yearFrom: 1950,
    yearTo: 2024,
    dateFrom: '',
    dateTo: '',
    sortBy: 'title'
  };

  // Categorías principales disponibles
  mainCategories: string[] = [
    'FOTOGRAFIAS',
    'PORTADAS'
  ];

  // Subcategorías obtenidas dinámicamente
  subCategories: SubCategory[] = [];

  // Variables para manejar categorías y subcategorías
  listCategoria: any = { response: { list: [] } };
  listSubCategoria: any = { response: { list: [] } };

  // Rangos de años disponibles
  minYear: number = 1900;
  maxYear: number = 2024;

  constructor(
    private router: Router,
    private novedadesService: NovedadesService
  ) { }

  ngOnInit(): void {
    // Inicializar años por defecto
    this.searchFilters.yearFrom = this.minYear;
    this.searchFilters.yearTo = this.maxYear;

    // Cargar categorías al inicializar
    this.loadCategories();
  }

  closeSearch(): void {
    this.close.emit();
  }

  loadCategories(): void {
    // Método placeholder para cargar categorías
    // Este método puede implementarse según la lógica de negocio necesaria
    // console.log('Loading categories...');
  }

  onSearch(): void {
    if (!this.isValidSearch()) {
      return;
    }

    const searchParams = this.buildSearchParams();
    this.searchPerformed.emit(searchParams);

    // Navegar a la página de resultados apropiada
    this.navigateToResults(searchParams);
    this.closeSearch();
  }

  private buildSearchParams(): any {
    const params: any = {
      q: this.searchFilters.query?.trim() || '',
      page: 1
    };

    // Añadir categoría principal si está seleccionada
    if (this.searchFilters.categoryName) {
      params.categoryName = this.searchFilters.categoryName;
    }

    // Añadir subcategoría si está seleccionada
    if (this.searchFilters.subCategoryName) {
      params.subCategoryName = this.searchFilters.subCategoryName;
    }

    // Añadir rango de años
    if (this.searchFilters.yearFrom !== this.minYear) {
      params.yearFrom = this.searchFilters.yearFrom;
    }
    if (this.searchFilters.yearTo !== this.maxYear) {
      params.yearTo = this.searchFilters.yearTo;
    }

    // Añadir rango de fechas
    if (this.searchFilters.dateFrom) {
      params.dateFrom = this.searchFilters.dateFrom;
    }
    if (this.searchFilters.dateTo) {
      params.dateTo = this.searchFilters.dateTo;
    }

    // Añadir ordenamiento
    if (this.searchFilters.sortBy !== 'title') {
      params.sort = this.searchFilters.sortBy;
    }

    return params;
  }

  private navigateToResults(params: any): void {
    // Determinar qué tipo de contenido buscar basado en los checkboxes
    const routes = [];

    if (this.searchFilters.includePhotos) {
      routes.push('fotografias');
    }
    if (this.searchFilters.includeCovers) {
      routes.push('portadas');
    }

    // Si solo hay un tipo seleccionado, navegar directamente a esa página
    if (routes.length === 1) {
      this.router.navigate([`/${routes[0]}`], { queryParams: params });
    } else {
      // Si hay múltiples tipos o ninguno, navegar a fotografías por defecto
      this.router.navigate(['/fotografias'], { queryParams: params });
    }
  }

  resetFilters(): void {
    this.searchFilters = {
      query: '',
      includePhotos: true,
      includeCovers: true,
      categoryName: '',
      subCategoryName: '',
      yearFrom: this.minYear,
      yearTo: this.maxYear,
      dateFrom: '',
      dateTo: '',
      sortBy: 'title'
    };
  }

  isValidSearch(): boolean {
    // Al menos debe tener una consulta o algún filtro activo
    return !!(
      this.searchFilters.query?.trim() ||
      this.searchFilters.categoryName ||
      this.searchFilters.subCategoryName ||
      this.searchFilters.yearFrom !== this.minYear ||
      this.searchFilters.yearTo !== this.maxYear
    );
  }

  getTotalResults(): string {
    // Simulación de conteo de resultados
    if (!this.isValidSearch()) {
      return '0';
    }

    const baseCount = this.searchFilters.query?.length || 0;
    const categoryCount = this.searchFilters.categoryName ? 1 : 0;
    const subCategoryCount = this.searchFilters.subCategoryName ? 1 : 0;
    const yearRangeCount = (this.searchFilters.yearFrom !== this.minYear || this.searchFilters.yearTo !== this.maxYear) ? 1 : 0;

    const estimated = Math.max(1, (baseCount * 10) + (categoryCount * 50) + (subCategoryCount * 30) + (yearRangeCount * 20));

    if (estimated > 1000) {
      return '1000+';
    }
    return estimated.toString();
  }

  // Métodos de utilidad para el template
  getSelectedContentTypes(): string[] {
    const types = [];
    if (this.searchFilters.includePhotos) types.push('fotografías');
    if (this.searchFilters.includeCovers) types.push('portadas');
    return types;
  }

  hasAnyContentTypeSelected(): boolean {
    return this.searchFilters.includePhotos || this.searchFilters.includeCovers;
  }

  // Método para manejar cambios en el slider de años
  onYearRangeChange(): void {
    // Este método puede ser usado para realizar validaciones o acciones adicionales
    // cuando cambie el rango de años
  }
}
